import { Fragment, useEffect } from 'react';

import BlogCategoryPage from '@/components/BasePages/BlogCategoryPage';
import BlogPage from '@/components/BasePages/BlogPage';
import DynamicRedirectPage from '@/components/BasePages/DynamicRedirectPage/DynamicRedirectPage';
import MultiProductPage from '@/components/BasePages/MultiProductPage/MultiProductPage';
import StandardPage from '@/components/BasePages/StandardPage';
import SunrunShopProductPage from '@/components/BasePages/SunrunShopProductPage/SunrunShopProductPage';
import ConstrainedExitIntent from '@/components/ExitIntent/ConstrainedExitIntent';
import type { EnahancedProductPageContent } from '@/contentful/api/enhancePages/productPage.types';
import {
  type BlogCategoryPageContentType,
  type BlogPageContent,
  type EnhancedStandardPageContent,
  type PageContent,
  PageContentType,
} from '@/contentful/contentful-exports';
import type { DynamicRedirectPageCF, MultiProductPageCF } from '@/types';

interface Props {
  content: PageContent;
}

interface IWindow extends Window {
  FS: (arg: string) => void;
}

export default function Page({
  content: {
    fields: { fullStoryEnabled, pageContent, seoFields, path, exitIntent },
  },
}: Props): React.JSX.Element | null {
  // biome-ignore lint/correctness/useExhaustiveDependencies: Automated migration to Biome
  useEffect(() => {
    const interval = setInterval(() => {
      if (fullStoryEnabled) {
        const isReady =
          typeof (window as unknown as IWindow)?.FS === 'function';

        if (isReady) {
          (window as unknown as IWindow).FS('start');
          clearInterval(interval);
        }
      }
    }, 5 * 1000);

    return () => clearInterval(interval);
  }, []);

  switch (pageContent?.sys?.contentType.sys.id) {
    case PageContentType.STANDARD:
      return (
        <Fragment>
          <StandardPage
            content={pageContent as EnhancedStandardPageContent}
            path={path}
            seoFields={seoFields}
          />
          {exitIntent && <ConstrainedExitIntent exitIntent={exitIntent} />}
        </Fragment>
      );
    case PageContentType.BLOG:
      return (
        <Fragment>
          <BlogPage
            path={path}
            seoFields={seoFields}
            content={pageContent as BlogPageContent}
          />
          {exitIntent && <ConstrainedExitIntent exitIntent={exitIntent} />}
        </Fragment>
      );
    case PageContentType.BLOG_CATEGORY:
      return (
        <Fragment>
          <BlogCategoryPage
            content={pageContent as BlogCategoryPageContentType}
          />
          {exitIntent && <ConstrainedExitIntent exitIntent={exitIntent} />}
        </Fragment>
      );
    case PageContentType.DYNAMIC_REDIRECT:
      return (
        <DynamicRedirectPage content={pageContent as DynamicRedirectPageCF} />
      );
    case PageContentType.SUNRUN_SHOP_PRODUCT:
      return (
        <SunrunShopProductPage
          content={pageContent as EnahancedProductPageContent}
        />
      );
    case PageContentType.MULTI_PRODUCT:
      return <MultiProductPage content={pageContent as MultiProductPageCF} />;
    default:
      return null;
  }
}
